import apiClient, { type ApiError } from './apiClient'
import { isAuthError, createAuthError, extractErrorMessage } from '@/lib/utils/errorHandling'
import { extractArrayFromResponse } from './utils/responseParser'
import { logDebug, logWarning, logError } from './utils/logger'
import { API_ENDPOINTS } from './config'
import type { StateResponse, State, GetStatesParams } from './statesService.types'

export type { StateResponse, State, GetStatesParams } from './statesService.types'

let statesCache: State[] | null = null
let statesPromise: Promise<State[]> | null = null

/** True after the first successful /states load (or in-flight fetch started). */
export function isStatesCacheReady(): boolean {
  return statesCache !== null
}

/**
 * Clears the in-memory states cache (test-only).
 * @internal
 */
export function resetStatesCacheForTests(): void {
  statesCache = null
  statesPromise = null
}

function filterStatesByZipFlag(states: State[], zipFlag: string): State[] {
  return states.filter((state) => state.zip_flag === zipFlag)
}

async function fetchStatesFromApi(): Promise<State[]> {
  try {
    const response = await apiClient.get<StateResponse[] | unknown>(API_ENDPOINTS.STATES)
    const responseData = response.data

    logDebug('States API Response:', responseData)

    const rawStates = extractArrayFromResponse<StateResponse>(responseData)

    if (rawStates.length === 0) {
      logWarning('No states returned from API')
      return []
    }

    const mappedStates: State[] = rawStates.map((item, index) => {
      if (!item || typeof item !== 'object') {
        logWarning('Invalid state item at index', index, item)
        return createFallbackState(index)
      }

      const apiItem = item as StateResponse

      if (apiItem.state_name && apiItem.state_code) {
        return {
          id: apiItem.id ?? index,
          state_name: apiItem.state_name,
          state_code: apiItem.state_code,
          zip_flag: apiItem.zip_flag,
          name: apiItem.state_name,
          abbreviation: apiItem.state_code,
        }
      }

      const legacyItem = item as {
        name?: string
        abbreviation?: string
        state?: string
        code?: string
        id?: number
        zip_flag?: string
      }
      if (legacyItem.name || legacyItem.state) {
        return {
          id: legacyItem.id ?? index,
          state_name: legacyItem.name || legacyItem.state || '',
          state_code: legacyItem.abbreviation || legacyItem.code || '',
          zip_flag: legacyItem.zip_flag,
          name: legacyItem.name || legacyItem.state || '',
          abbreviation: legacyItem.abbreviation || legacyItem.code || '',
        }
      }

      logWarning('Invalid state format at index', index, item)
      return createFallbackState(index)
    })

    logDebug(`Mapped ${mappedStates.length} states`)
    return mappedStates
  } catch (error) {
    logError('Failed to fetch states:', error)

    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to fetch states.'),
      status: (error as ApiError).status || 500,
    } as ApiError
  }
}

/**
 * Cached GET /states — mirrors Angular NgRx `LoadState` (fetch once, reuse).
 * When `zip_flag` is requested, filters the cached full list client-side like Angular `listfilter`.
 */
export async function loadStates(params?: GetStatesParams): Promise<State[]> {
  if (!statesPromise) {
    if (import.meta.env.DEV) {
      console.debug('[referenceData] GET /states')
    }
    statesPromise = fetchStatesFromApi()
      .then((states) => {
        statesCache = states
        return states
      })
      .catch((error) => {
        statesPromise = null
        throw error
      })
  }

  const allStates = statesCache ?? (await statesPromise)

  if (params?.zip_flag !== undefined && params.zip_flag !== '') {
    return filterStatesByZipFlag(allStates, params.zip_flag)
  }

  return allStates
}

/**
 * Get states from API
 * @returns Array of states
 * @throws ApiError if request fails
 */
export async function getStates(params?: GetStatesParams): Promise<State[]> {
  try {
    const response = await apiClient.get<StateResponse[] | unknown>(API_ENDPOINTS.STATES, {
      params:
        params?.zip_flag !== undefined && params.zip_flag !== ''
          ? { zip_flag: params.zip_flag }
          : undefined,
    })
    const responseData = response.data

    logDebug('States API Response:', responseData)

    // Extract array from various response formats
    const rawStates = extractArrayFromResponse<StateResponse>(responseData)

    if (rawStates.length === 0) {
      logWarning('No states returned from API')
      return []
    }

    // Map API response to application format
    const mappedStates: State[] = rawStates.map((item, index) => {
      // Validate required fields
      if (!item || typeof item !== 'object') {
        logWarning('Invalid state item at index', index, item)
        return createFallbackState(index)
      }

      const apiItem = item as StateResponse

      // Primary format: { state_name, state_code }
      if (apiItem.state_name && apiItem.state_code) {
        return {
          id: apiItem.id ?? index,
          state_name: apiItem.state_name,
          state_code: apiItem.state_code,
          name: apiItem.state_name,
          abbreviation: apiItem.state_code,
        }
      }

      // Legacy format support
      const legacyItem = item as { name?: string; abbreviation?: string; state?: string; code?: string; id?: number }
      if (legacyItem.name || legacyItem.state) {
        return {
          id: legacyItem.id ?? index,
          state_name: legacyItem.name || legacyItem.state || '',
          state_code: legacyItem.abbreviation || legacyItem.code || '',
          name: legacyItem.name || legacyItem.state || '',
          abbreviation: legacyItem.abbreviation || legacyItem.code || '',
        }
      }

      // Fallback for invalid data
      logWarning('Invalid state format at index', index, item)
      return createFallbackState(index)
    })

    logDebug(`Mapped ${mappedStates.length} states`)
    return mappedStates
  } catch (error) {
    logError('Failed to fetch states:', error)

    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to fetch states.'),
      status: (error as ApiError).status || 500,
    } as ApiError
  }
}

/**
 * Create a fallback state for invalid data
 */
function createFallbackState(index: number): State {
  return {
    id: index,
    state_name: '',
    state_code: '',
    name: '',
    abbreviation: '',
  }
}

/**
 * Map API / persisted state value to a dropdown value (state_code).
 * Handles 2-letter codes, full names, and casing — same idea as Angular select value="{{state.code}}".
 */
export function resolveStateToCode(raw: unknown, states: State[]): string {
  if (raw === null || raw === undefined) return ''
  const trimmed = String(raw).trim()
  if (!trimmed) return ''
  if (!states.length) return trimmed

  const upper = trimmed.toUpperCase()
  const byExactCode = states.find(
    (s) => s.state_code && (s.state_code === trimmed || s.state_code.toUpperCase() === upper),
  )
  if (byExactCode?.state_code) return byExactCode.state_code

  const byName = states.find(
    (s) =>
      s.state_name &&
      (s.state_name.toLowerCase() === trimmed.toLowerCase() ||
        s.name?.toLowerCase() === trimmed.toLowerCase()),
  )
  if (byName?.state_code) return byName.state_code

  const byAbbrev = states.find((s) => s.abbreviation && s.abbreviation.toUpperCase() === upper)
  if (byAbbrev?.state_code) return byAbbrev.state_code

  return upper.length === 2 ? upper : trimmed
}
